import {
  ExportFailureIcon,
  ExportLoadingIcon,
  ExportSuccessfulIcon,
} from 'components/common/Icons';
import { FC, ReactNode } from 'react';
import { RequestStatus } from 'utils/constants/RequestStatus';
import { IconProps, TranslateFn, TranslateReactFn } from 'utils/types';

export const SHADOW = {
  small:
    'rgb(61 58 180 / 40%) 0px 1px 2px 0px, rgb(61 58 180 / 10%) 0px -1px 2px 0px',
  medium:
    'rgb(61 58 180 / 15%) 0px 4px 5px 0px, rgb(61 58 180 / 10%) 0px -1px 2px 0px',
  large:
    'rgb(61 58 180 / 15%) 0px 6px 14px 0px, rgb(61 58 180 / 10%) 0px -1px 2px 0px',
};

interface IGetStatusMetaData {
  translateFn: TranslateFn;
  translateReactFn: TranslateReactFn;
  exportType: 'async' | 'sync';
  status: RequestStatus;
}

interface StatusMetaData {
  title: string;
  description: string | ReactNode | ((userEmail?: string) => JSX.Element);
  icon: FC<IconProps>;
}

export const getStatusMetaData = ({
  translateFn: t,
  translateReactFn: t__react,
  exportType,
  status,
}: IGetStatusMetaData): StatusMetaData => {
  if (exportType === 'async') {
    switch (status) {
      case RequestStatus.Loading: {
        return {
          title: t('core.exportInProgress'),
          description: t('core.exportInProgressDescription', {
            boldText: t('global.doNot'),
          }),
          icon: ExportLoadingIcon,
        };
      }
      case RequestStatus.Success: {
        return {
          title: t('core.asyncExportSuccessful'),
          description: t('core.asyncExportSuccessfulDescription'),
          icon: ExportSuccessfulIcon,
        };
      }
      case RequestStatus.PermissionError: {
        return {
          title: t('core.asyncExportPermissionError'),
          description: (userEmail: string) => {
            return t__react('core.asyncExportPermissionErrorDescription', {
              errorTitle: t('core.accessFileError'),
              errorDescription: t__react('core.signedAs', {
                email: <b>{userEmail}</b>,
              }),
            });
          },
          icon: ExportFailureIcon,
        };
      }
      case RequestStatus.ExpirationError: {
        return {
          title: t('core.asyncExportExpired'),
          description: t('core.asyncExportExpiredDescription'),
          icon: ExportFailureIcon,
        };
      }
    }
  }

  switch (status) {
    case RequestStatus.Loading: {
      return {
        title: t('core.exportInProgress'),
        description: t__react('core.exportInProgressDescription', {
          boldText: t('global.doNot'),
        }),
        icon: ExportLoadingIcon,
      };
    }
    case RequestStatus.Success: {
      return {
        title: t('core.syncExportSuccessful'),
        description: t('core.syncExportSuccessfulDescription'),
        icon: ExportSuccessfulIcon,
      };
    }
    case RequestStatus.Failure: {
      return {
        title: t('core.syncExportFailed'),
        description: t__react('core.syncExportFailedDescription', {
          boldText: t('core.aFewMinutes'),
        }),
        icon: ExportFailureIcon,
      };
    }
    case RequestStatus.Timeout: {
      return {
        title: t('core.syncExportTimeout'),
        description: t('core.syncExportTimeoutDescription'),
        icon: ExportFailureIcon,
      };
    }
  }

  return {
    title: '',
    description: '',
    icon: ExportFailureIcon,
  };
};
